import { FC } from 'react';
import { Block, Header, InfoBlock, List, ListItem, ScrollView } from '$uikit';
import { useModalClose, useCurrentUserInfo, useTranslator } from '$hooks';
import { goBack, showModal } from '$navigation/helper';
import './AdventCalendarConfiguration.sass';
import { IcGallery28, IcCheck28 } from '$assets';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '$store/rootReducer';
import { adventCalendarSelector, AdventCalendarDay } from '$store/adventCalendar';
import { formatCurrency } from 'utils';
import { CurrencyEnum } from '$store/models';
import { toastActions } from '$store/toast';

export const AdventCalendarConfiguration: FC = () => {
  const dispatch = useDispatch();
  const currentUser = useCurrentUserInfo();
  const t = useTranslator();
  const adventCalendar = useSelector((state: RootState) =>
    adventCalendarSelector(state, currentUser?.id!),
  );

  const handleClose = () => {
    goBack();
    return false;
  };

  useModalClose('advent_calendar_configuration', () => {
    return handleClose();
  });

  const days = adventCalendar?.days || [];
  const isPublic = adventCalendar?.isPublic;

  const allDaysFilled = days.every((day) => day && day.bundleId);
  const hasDeclinedDays = days.some((day) => day && day.moderated === 2);
  const hasPendingDays = days.some((day) => day && day.moderated === 0);

  const handleDayClick = (dayData: AdventCalendarDay | null, index: number) => {
    if (isPublic) {
      return dispatch(toastActions.setToastSuccess(t('advent_calendar_editing_disabled')));
    }

    const bundleId = dayData?.bundleId;
    const modalName = bundleId
      ? `edit_advent_bundle-${index + 1}-${bundleId}`
      : `create_advent_bundle-${index + 1}`;
    showModal(modalName, false);
  };

  const getDaySubtitle = (dayData: AdventCalendarDay | null): string => {
    if (!dayData || !dayData.bundleId) {
      return t('advent_setup_button');
    }

    if (!dayData.price || dayData.price === 0) {
      return t('advent_free');
    }

    return formatCurrency(dayData.price, CurrencyEnum.EUR);
  };

  const getModerationStatus = (
    dayData: AdventCalendarDay | null,
  ): { text: string; className: string } | null => {
    if (!dayData || !dayData.bundleId) {
      return null;
    }

    switch (dayData.moderated) {
      case 2:
        return { text: t('advent_status_declined'), className: 'declined' };
      case 1:
        return { text: t('advent_status_approved'), className: 'approved' };
      default:
        return null;
    }
  };

  const getPreviewImage = (dayData: AdventCalendarDay | null): string => {
    if (!dayData?.previews || dayData.previews.length === 0) {
      return '';
    }

    const firstPreview = dayData.previews[0];
    let coverUrl: string | undefined;
    let thumbnail: string | undefined;

    if (firstPreview.type === 'photo' && firstPreview.photo) {
      coverUrl = firstPreview.photo.photoMedium || firstPreview.photo.photoLarge;
      thumbnail = firstPreview.photo.thumbnail;
    } else if (firstPreview.type === 'video' && firstPreview.video) {
      coverUrl = firstPreview.video.photoMedium || firstPreview.video.photoLarge;
      thumbnail = firstPreview.video.thumbnail;
    }

    return [coverUrl, thumbnail]
      .filter(Boolean)
      .map((u) => `url(${u})`)
      .join(', ');
  };

  const renderInfo = () => {
    if (adventCalendar?.isActive && isPublic) {
      return (
        <InfoBlock type="success" icon={<IcCheck28 />}>
          {t('advent_status_active')}
        </InfoBlock>
      );
    }

    if (hasDeclinedDays) {
      return <InfoBlock type="warning">{t('advent_status_needs_correction')}</InfoBlock>;
    }

    if (hasPendingDays && allDaysFilled) {
      return <InfoBlock type="info">{t('advent_status_in_moderation')}</InfoBlock>;
    }

    return <InfoBlock type="info">{t('advent_status_fill_all_days')}</InfoBlock>;
  };

  return (
    <>
      <Header showBackButton backButtonOnClick={handleClose}>
        {t('advent_header_february_14')}
      </Header>

      <ScrollView>
        <div className="AdventCalendarConfiguration__content">
          <Block>{renderInfo()}</Block>
          <List>
            {days.map((dayData, index) => {
              const dayNumber = index + 1;
              const preview = getPreviewImage(dayData);
              const moderationStatus = getModerationStatus(dayData);

              return (
                <ListItem
                  key={dayNumber}
                  leftIcon={
                    <div
                      className="AdventCalendarConfiguration__day-icon"
                      style={
                        preview
                          ? {
                              backgroundImage: preview,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }
                          : undefined
                      }
                    >
                      {!preview && <IcGallery28 />}
                    </div>
                  }
                  leftIconSize="large"
                  chevron={!isPublic}
                  onClick={() => handleDayClick(dayData, index)}
                  after={
                    moderationStatus ? (
                      <div
                        className={`AdventCalendarConfiguration__moderation AdventCalendarConfiguration__moderation--${moderationStatus.className}`}
                      >
                        {moderationStatus.text}
                      </div>
                    ) : undefined
                  }
                >
                  <div className="AdventCalendarConfiguration__day-content">
                    <div className="AdventCalendarConfiguration__day-title">
                      {t('advent_day', { day: dayNumber })}
                    </div>
                    <div className="AdventCalendarConfiguration__day-subtitle">
                      {getDaySubtitle(dayData)}
                    </div>
                  </div>
                </ListItem>
              );
            })}
          </List>
        </div>
      </ScrollView>
    </>
  );
};
